import React from "react";
import { Helmet } from "react-helmet"; // npm install react-helmet

interface TimelineEvent {
  year: string;
  title: string;
  description: string;
  icon?: string;
  link?: string; // optional internal link for SEO
}

const History = () => {
  const timelineEvents: TimelineEvent[] = [
    {
      year: "2001",
      title: "Founded Anestar Victory",
      description:
        "Anestar Victory Junior & Senior School was established with the motto 'Legacy of Excellence.' The school was founded on principles of academic excellence, character development, and holistic education. From its inception, Anestar Victory committed to providing world-class education in a safe, loving, and caring environment.",
      icon: "🏫",
    },
    {
      year: "2001-2010",
      title: "Foundation & Growth Phase",
      description:
        "During the first decade, Anestar Victory established itself as a leading educational institution in Nakuru. The school built strong foundations with experienced educators, modern facilities, and a commitment to the CHICEM core values (Christ-centeredness, Integrity, Compassion, Hard Work, Excellence, Men of Valor). The school expanded from initial enrollment to become a preferred choice for parents seeking quality education.",
      icon: "📚",
    },
    {
      year: "2010-2015",
      title: "Expansion & Infrastructure Development",
      description:
        "This period saw significant expansion of Anestar Victory's physical infrastructure and academic programs. The school invested in modern science laboratories, computer facilities, and sports amenities. The agricultural program was introduced, emphasizing sustainability and practical learning. The school began establishing itself as part of the broader Anestar Group of Schools, strengthening its network and resources.",
      icon: "🏗️",
    },
    {
      year: "2015-2020",
      title: "Academic Excellence & Recognition",
      description:
        "Anestar Victory gained recognition for consistent academic excellence. The school's commitment to the Competence-Based Education (CBC) curriculum ensured students developed critical thinking and problem-solving skills. Alumni began excelling in various fields including medicine, engineering, aviation, and business, reflecting the school's success in nurturing capable and purpose-driven individuals.",
      icon: "🎓",
    },
    {
      year: "2020-2023",
      title: "Digital Transformation & Resilience",
      description:
        "During the COVID-19 pandemic, Anestar Victory demonstrated resilience by implementing digital learning solutions while maintaining educational quality. The school invested in technology infrastructure, enabling seamless online and hybrid learning. The school continued to emphasize holistic development, ensuring students' mental, physical, and emotional well-being alongside academic growth.",
      icon: "💻",
    },
    {
      year: "2024-Present",
      title: "National Recognition & Sustainability Leadership",
      description:
        "Anestar Boys High School, part of the Anestar Group, achieved #11 national ranking with an impressive 9.7 mean grade, demonstrating the group's commitment to excellence. The agricultural program has become a flagship initiative, with a farm-to-table approach producing sukuma wiki, cabbages, potatoes, and raising 300+ Kienyeji chickens and turkeys. This initiative teaches sustainability, food security, and practical agricultural skills while providing nutritious meals for students.",
      icon: "🌱",
    },
  ];

  return (
    <section className="flex flex-col justify-between h-full space-y-5 rounded-xl bg-gradient-to-br from-white to-slate-50 dark:from-slate-800 dark:to-slate-900 p-6 shadow-lg hover:shadow-xl hover:-translate-y-1 transition-transform">
      
      {/* SEO Meta Tags & Open Graph */}
      <Helmet>
        <title>Anestar Boys High School – Lanet Victory Campus | Our History</title>
        <meta
          name="description"
          content="Discover the history of Anestar Boys High School – Lanet Victory Campus, established in 2001. Explore milestones, achievements, and sustainability programs in Nakuru, Kenya."
        />
        <meta
          name="keywords"
          content="Anestar Boys High School, Lanet Victory Campus, Anestar history, Kenya secondary schools, CBC curriculum, holistic education"
        />
        <meta name="robots" content="index, follow" />
        {/* Open Graph */}
        <meta property="og:title" content="Anestar Boys High School – Lanet Victory Campus | Our History" />
        <meta property="og:description" content="Explore the milestones, achievements, and sustainability programs of Anestar Boys High School – Lanet Victory Campus in Nakuru, Kenya." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.anestarjss.com/history" />
        <meta property="og:image" content="https://www.anestarjss.com/assets/images/lanet-campus.jpg" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Anestar Boys High School – Lanet Victory Campus | Our History" />
        <meta name="twitter:description" content="Explore the milestones, achievements, and sustainability programs of Anestar Boys High School – Lanet Victory Campus in Nakuru, Kenya." />
        <meta name="twitter:image" content="https://www.anestarjss.com/assets/images/lanet-campus.jpg" />
      </Helmet>

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "EducationalOrganization",
          "name": "Anestar Boys High School – Lanet Victory Campus",
          "url": "https://www.anestarjss.com",
          "foundingDate": "2001",
          "foundingLocation": {
            "@type": "Place",
            "name": "Lanet, Nakuru, Kenya",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Lanet Ol-Kalou Rd",
              "addressLocality": "Nakuru",
              "postalCode": "273",
              "addressCountry": "KE"
            }
          },
          "description": "Anestar Boys High School – Lanet Victory Campus, founded in 2001, offers world-class secondary education in Nakuru, Kenya. It emphasizes competence-based learning, holistic education, and sustainability programs.",
          "motto": "Legacy of Excellence",
          "educationalLevel": "Secondary",
          "areaServed": "Nakuru, Kenya",
          "event": timelineEvents.map(ev => ({
            "@type": "EducationalEvent",
            "name": ev.title,
            "startDate": ev.year.split("-")[0],
            "description": ev.description
          }))
        })}
      </script>

      {/* Main Content */}
      <div className="flex flex-col space-y-6">
        <div>
          <h2 className="text-3xl font-bold text-primary mb-2">
            Our History: A Legacy of Excellence
          </h2>
          <p className="text-slate-600 dark:text-slate-400 text-sm">
            Anestar Victory Junior & Senior School has been providing world-class
            education since 2001, shaping generations of capable and purpose-driven
            individuals.
          </p>
        </div>

        <article className="space-y-6">
          <ol className="space-y-6 relative">
            <div className="absolute left-1.5 top-0 bottom-0 w-px bg-gradient-to-b from-primary via-primary to-transparent" aria-hidden="true"/>
            {timelineEvents.map((event, index) => (
              <li key={index} className="relative pl-8 pb-4" itemScope itemType="https://schema.org/Event">
                <div className="absolute left-0 top-1 w-4 h-4 bg-primary rounded-full shadow-md ring-4 ring-white dark:ring-slate-800 animate-pulse" aria-hidden="true"/>
                <div className="bg-white dark:bg-slate-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <time dateTime={event.year} className="inline-block bg-primary text-white text-xs font-bold px-3 py-1 rounded-full mb-2" itemProp="startDate">
                    {event.icon} {event.year}
                  </time>
                  <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-2" itemProp="name">
                    {event.title}
                  </h3>
                  <p className="text-slate-700 dark:text-slate-300 leading-relaxed text-sm" itemProp="description">
                    {event.description}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </article>

        <section className="mt-8 pt-8 border-t border-slate-200 dark:border-slate-700">
          <h3 className="text-xl font-bold text-primary mb-4">
            Key Achievements & Impact
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-blue-50 dark:bg-slate-700 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">🏆 National Recognition</h4>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Anestar Boys High School achieved #11 national ranking with a 9.7 mean grade, demonstrating our commitment to academic excellence and quality education.
              </p>
            </div>

            <div className="bg-green-50 dark:bg-slate-700 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">🌱 Sustainability Leadership</h4>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Our farm-to-table agricultural program produces fresh vegetables and raises livestock, teaching students practical skills while ensuring food security and environmental sustainability.
              </p>
            </div>

            <div className="bg-purple-50 dark:bg-slate-700 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">👥 Alumni Excellence</h4>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                Our alumni have excelled in medicine, engineering, aviation, business, and public service, reflecting our success in nurturing capable and purpose-driven individuals.
              </p>
            </div>

            <div className="bg-amber-50 dark:bg-slate-700 rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">🎓 Holistic Education</h4>
              <p className="text-sm text-slate-700 dark:text-slate-300">
                We provide world-class education combining academic excellence with character development, sports, arts, and practical life skills.
              </p>
            </div>
          </div>
        </section>
      </div>
    </section>
  );
};

export default History;
